import { useState } from 'react';
import {
    Alert, Box, Button, Chip, FormControl, FormLabel, Input, Sheet, Stack,
    Tab, TabList, Tabs, Table, Typography,
} from '@mui/joy';

import { useAdmin } from '../../AdminContext';
import { APIAdminIdentitySearch, APIAdminPhotoSearch, antifraudError } from '../../api_admin_antifraud';

const STATUS_SHORT = { pending: 'на проверке', approved: 'подтв.', rejected: 'откл.', changes_requested: 'правки' };

function ResultPhoto({ url }) {
    return url ? <img src={url} alt="" width={40} height={40} style={{ objectFit: 'cover', borderRadius: 6 }} /> : null;
}

// -------- Поиск по ФИО / дате рождения --------
function IdentitySearch() {
    const { adminToken: token } = useAdmin();
    const [form, setForm] = useState({ full_name: '', birth_date: '', city: '' });
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const search = async () => {
        if (!form.full_name && !form.birth_date && !form.city) { setError('Заполните хотя бы одно поле'); return; }
        setLoading(true); setError(null); setItems(null);
        try { const r = await APIAdminIdentitySearch(token, form); setItems(r?.items || []); }
        catch (e) { setError(antifraudError(e)); } finally { setLoading(false); }
    };

    return (
        <Box>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-end">
                    <FormControl sx={{ flex: 1 }}><FormLabel>ФИО</FormLabel>
                        <Input placeholder="Иванов Иван" value={form.full_name}
                               onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                               onKeyDown={(e) => e.key === 'Enter' && search()} /></FormControl>
                    <FormControl><FormLabel>Дата рождения</FormLabel>
                        <Input type="date" value={form.birth_date}
                               onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></FormControl>
                    <FormControl><FormLabel>Город</FormLabel>
                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FormControl>
                    <Button loading={loading} onClick={search}>Найти</Button>
                </Stack>
            </Sheet>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {items !== null && (
                items.length === 0 ? <Typography level="body-sm" color="neutral">Ничего не найдено.</Typography> : (
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead><tr><th style={{ width: 56 }}></th><th>Анкета</th><th>ФИО</th><th style={{ width: 120 }}>Дата рожд.</th><th style={{ width: 130 }}>Город</th><th style={{ width: 130 }}>Верификация</th></tr></thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.verification_id || it.profile_id}>
                                        <td><ResultPhoto url={it.profile_photo_url} /></td>
                                        <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{it.profile_name}</Typography></td>
                                        <td>{it.full_name || '—'}</td>
                                        <td>{it.birth_date || '—'}</td>
                                        <td>{it.city || '—'}</td>
                                        <td>{it.is_verified
                                            ? <Chip size="sm" color="success" variant="soft">верифиц.</Chip>
                                            : <Chip size="sm" color="neutral" variant="soft">{STATUS_SHORT[it.status] || it.status}</Chip>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                )
            )}
        </Box>
    );
}

// -------- Поиск по фото --------
function PhotoSearch() {
    const { adminToken: token } = useAdmin();
    const [file, setFile] = useState(null);
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const search = async () => {
        if (!file) { setError('Выберите изображение'); return; }
        setLoading(true); setError(null); setItems(null);
        try {
            const fd = new FormData(); fd.append('image', file);
            const r = await APIAdminPhotoSearch(token, fd); setItems(r?.items || []);
        } catch (e) { setError(antifraudError(e)); } finally { setLoading(false); }
    };

    return (
        <Box>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <Button loading={loading} onClick={search}>Найти по фото</Button>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                    Ищем анкеты с похожим фото по перцептивному хэшу (нужен imagehash на сервере).
                </Typography>
            </Sheet>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {items !== null && (
                items.length === 0 ? <Typography level="body-sm" color="neutral">Похожих фото не найдено.</Typography> : (
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead><tr><th style={{ width: 56 }}></th><th>Анкета</th><th style={{ width: 130 }}>Город</th><th style={{ width: 120 }}>Похожесть</th><th style={{ width: 120 }}>Статус</th></tr></thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.profile_id}>
                                        <td><ResultPhoto url={it.photo_url} /></td>
                                        <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{it.name}</Typography></td>
                                        <td>{it.city || '—'}</td>
                                        <td>расст. {it.distance}</td>
                                        <td>{it.is_verified && <Chip size="sm" color="success" variant="soft">верифиц.</Chip>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                )
            )}
        </Box>
    );
}

export default function AdminAntifraudPage() {
    const [tab, setTab] = useState(0);
    return (
        <Box>
            <Typography level="h4" sx={{ mb: 2 }}>Антифрод и поиск</Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent', mb: 2 }}>
                <TabList>
                    <Tab>По ФИО / дате рождения</Tab>
                    <Tab>По фото</Tab>
                </TabList>
            </Tabs>
            {tab === 0 && <IdentitySearch />}
            {tab === 1 && <PhotoSearch />}
        </Box>
    );
}